import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Grid,
  IconButton,
  TextField,
  Button,
} from '@mui/material';
import { ChatBubbleOutline as ChatBubbleOutlineIcon, Edit as EditIcon, Delete as DeleteIcon, PushPin as PushPinIcon } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import { useSelector } from 'react-redux';
import { createComment, deleteComment, getComments, updateComment } from '../../api/commentsApi';
import { updateDiscussion } from '../../api/discussion api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DiscussionCard = ({ discussion, handleDelete, onPinToggle }) => {
  const user = useSelector(state => state.users);
  const [newComment, setNewComment] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [isCommentsVisible, setIsCommentsVisible] = useState(false);
  const [editingDiscussion, setEditingDiscussion] = useState(false);
  const [editedTitle, setEditedTitle] = useState(discussion.title);
  const [editedDescription, setEditedDescription] = useState(discussion.description);

  const handleToggleComments = () => setIsCommentsVisible(prev => !prev);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await getComments(discussion.id);
        // console.log('Comments fetched:', response); // Debugging
        setComments(response);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchComments();
  }, [discussion.id]);

  // Handle adding a new comment
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      setError("Comment cannot be empty.");
      toast.error("Comment cannot be empty.");
      return;
    }
    const data = {
      name: user.name,
      userId: user.id,
      Id: comments.id,
      discussionId: discussion.id,
      message: newComment.trim(),
      profileImage: user.profileImage || "/logo.png",
      role: user.role || "User"
    };

    try {
      const response = await createComment(discussion.id, data);
      setNewComment('');
      toast.success("Comment added");
      console.log('Comment added:', response); // Debugging
      if (response && response.data && response.data.id) {
        const newCommentId = response.data.id;
        setComments(prevComments => [...prevComments, { ...data, id: newCommentId }]);

        setError('');
      }
    } catch (err) {
      setError(err.message);
      toast.error("Error adding comment: " + err.message);
    }
  };

  // Handle updating an existing comment
  const handleUpdateComment = async (id) => {
    if (!editingCommentText.trim()) {
      toast.error("Comments content is required to update the comment");
      return;
    }
    try {
      await updateComment(id, { comments: editingCommentText });
      setComments(prev =>
        prev.map(comment =>
          comment.id === id ? { ...comment, comments: editingCommentText } : comment
        )
      );
      setEditingCommentId(null);
      setEditingCommentText('');
      toast.success("Comment updated");
    } catch (error) {
      toast.error("Error updating comment: " + error.message);
    }
  };

  // Handle deleting a comment
  const handleDeleteComment = async (id) => {
    try {
      await deleteComment(id);
      setComments(prev => prev.filter(comment => comment.id !== id));
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment: " + error.message);
    }
  };

  // Handle discussion update
  const handleUpdateDiscussion = async () => {
    if (!editedTitle.trim() || !editedDescription.trim()) {
      setError("Title and description cannot be empty.");
      toast.error("Title and description cannot be empty.");
      return;
    }
    const discussionData = {
      title: editedTitle.trim(),
      description: editedDescription.trim(),
    };
    try {
      const response = await updateDiscussion(discussion.id, discussionData);
      if (response) {
        toast.success("Discussion updated");
        setEditingDiscussion(false);
      } else {
        throw new Error(response.message || "Failed to update discussion");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
  };

  const handleLikeUpdate = async (likes) => {
    const discussionData = {
      likes: likes + 1
    }
    await updateDiscussion(discussion.id, discussionData);
  }
  // Handle discussion delete
  const handleDeleteDiscussion = () => {
    handleDelete(discussion.id);
    toast.success("Discussion deleted");
  };

  // Handle pin/unpin toggle
  const handlePinClick = () => {
    onPinToggle(discussion?.id);
    toast.success(discussion.isPinned ? 'Discussion Unpinned' : 'Discussion Pinned');
  };

  const parsedComments = Array.isArray(comments) ? comments : (JSON.parse(comments) || [])

  return (
    <Box sx={{ border: '1px solid #cce5ff', borderRadius: '8px', padding: '2vh 2vw', boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', width: { xs: '95vw', sm: '85vw' }, margin: 'auto', display: 'flex', flexDirection: 'column', position: 'relative', transition: 'transform 0.2s, box-shadow 0.2s', background: 'linear-gradient(135deg, #f0f4ff, #d9e6ff)' }}>
      {user.id === discussion.userId ? (
        <Box sx={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '10px' }}>
          <IconButton onClick={() => setEditingDiscussion(true)} sx={{ color: '#007bff' }}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={handleDeleteDiscussion} sx={{ color: '#f44336' }}>
            <DeleteIcon />
          </IconButton>
          <IconButton onClick={handlePinClick}>
            {discussion.isPinned ? (
              <PushPinIcon sx={{ color: 'Green' }} />
            ) : (
              <PushPinOutlinedIcon sx={{ color: '#007bff' }} />
            )}
          </IconButton>
        </Box>
      ) : null}

      <Grid container spacing={2}>
        <Grid item>
          <Avatar
            src={discussion.profileImage || "/logo.png"}
            variant="square"
            alt={discussion?.userName}
            sx={{ width: '2.5vw', height: 'auto' }}
          />
        </Grid>
        <Grid item xs={10}>
          {editingDiscussion ? (
            <>
              <TextField value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} size="small" variant="outlined" error={!!error} helperText={error} fullWidth />
              <TextField value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} size="small" variant="outlined" error={!!error} helperText={error} fullWidth multiline rows={4} sx={{ marginTop: '1vh' }} />
              <Button onClick={handleUpdateDiscussion} color="primary" sx={{ marginTop: '1vh' }}>
                Save
              </Button>
              <Button onClick={() => setEditingDiscussion(false)} color="secondary" sx={{ marginLeft: '1vw', marginTop: '1vh' }}>Cancel</Button>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                <LabelImportantIcon sx={{ color: 'green', marginRight: '1vw' }} />
                {discussion.title}

              </Typography>
              <Typography variant="h5" sx={{ color: '#6c757d', marginTop: '1vh' }}>
                {discussion.description}
              </Typography>
            </>
          )}
          <Typography variant="h6" sx={{ color: '#007bff', cursor: 'pointer' }}>
            {discussion.topic}
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '2vh', ml: '5vw' }}
      >
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', color: '#6c757d' }}>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', marginRight: '2vw' }}>
            By <Typography sx={{ fontWeight: 'bold', marginLeft: '5px' }}>{discussion.userName}</Typography>
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: '1rem', marginRight: '5px' }} />
            {new Date(discussion.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Grid>

        <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <IconButton>
            <VisibilityIcon />
          </IconButton>
          <Typography variant="body2" sx={{ marginRight: '1vw', color: 'black' }}>
            {discussion.views}
          </Typography>
          <IconButton>
            <ThumbUpIcon onClick={() => handleLikeUpdate(discussion.likes)} />
          </IconButton>
          <Typography variant="body2" sx={{ marginRight: '1vw', color: 'black' }} >
            {discussion.likes}
          </Typography>
          <IconButton onClick={handleToggleComments}>
            <ChatBubbleOutlineIcon />
          </IconButton>
          <Typography variant="body2" sx={{ marginRight: '5vw', color: 'black' }}>
            {parsedComments.length}
          </Typography>
        </Grid>
      </Grid>
      {isCommentsVisible && (
        <Box sx={{ padding: '2vh', marginTop: '2vh', border: '1px solid #e1e1e1', borderRadius: '10px', backgroundColor: '#ffffff' }}>
          <Typography variant="h5" sx={{ marginBottom: '1vh', fontWeight: '600', color: '#333' }}>
            Comments
          </Typography>
          <Box sx={{ maxHeight: '250px', overflowY: 'auto', mb: '1vh', border: '1px solid #e1e1e1', borderRadius: '8px', padding: '1vh' }}>
            {parsedComments.length > 0 ? (
              parsedComments.map(comment => (
                <Box key={comment.id} sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', mt: '1vh', padding: '1vh', borderRadius: '8px', background: '#f9f9f9', transition: 'background 0.3s', '&:hover': { background: '#f1f1f1' } }}>
                  <Avatar src={comment.profileImage || "/logo.png"} alt={comment.name} sx={{ width: '40px', height: '40px', border: '2px solid #007bff' }} />
                  <Box sx={{ flex: 1, marginLeft: '1vh' }}>
                    {editingCommentId === comment.id ? (
                      <>
                        {/* Use editingCommentText for the edit input */}
                        <TextField
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          size="small"
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={2}
                        />
                        <Button onClick={() => handleUpdateComment(comment.id)} color="primary" sx={{ marginTop: '1vh' }}>
                          Save
                        </Button>
                        <Button onClick={() => setEditingCommentId(null)} color="secondary" sx={{ marginLeft: '1vw', marginTop: '1vh' }}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography sx={{ fontWeight: 'bold', color: '#007bff' }}>{comment?.name || 'Anonymous'} ({comment.role || 'User'})</Typography>
                        <Typography variant="body2" sx={{ color: '#555', marginBottom: '0.5vh' }}>{comment.comments}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#999' }}>
                          <Typography variant="h3" sx={{ fontSize: '0.8rem' }}><CalendarTodayIcon sx={{ fontSize: '1rem', marginRight: '5px' }} />{new Date(comment.createdAt).toLocaleDateString()}</Typography>
                        </Box>
                      </>
                    )}
                  </Box>


                  {user.id === comment.userId && (
                    <Box sx={{ position: 'absolute', top: '5px', right: '10px' }}>
                      <IconButton onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditingCommentText(comment.comments); // Set the current comment's text in edit field
                      }} sx={{ color: '#007bff' }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteComment(comment.id)} sx={{ color: '#f44336' }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>)}
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: '#999', textAlign: 'center' }}>No comments yet</Typography>
            )}
          </Box>

          {/* This TextField is only for adding new comments */}
          <TextField
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            size="small"
            variant="outlined"
            placeholder="Add a comment..."
            fullWidth
            error={!!error}
            helperText={error}
          />
          <Button onClick={handleAddComment} color="primary" sx={{ marginTop: '1vh' }}>
            Add Comment
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DiscussionCard;
